<template>
  <div
    :class="[(!createOrEdit && !qrCode) ? 'vx-card' : '', 'p-6']">
      <reviews-settings-create-or-edit
      v-if="createOrEdit"
      class="mb-base"
      @cancel="createOrEdit = false"/>

    <template v-else-if="qrCode">
      <vx-card
        remove-card-action
        remove-card-icon-svg-actions="w-6 h-6"
        class="vx-card p-6"
        @remove="() => $router.push({ name: 'reviews' })"
      >
        <div class="vx-row py-5 flex-col-reverse md:flex-row flex items-center">
          <div class="vx-col w-full md:w-1/2 mt-base md:mt-0">
            <reviews-q-r-code :size="250"/>
          </div>
          <div class="vx-col w-full md:w-1/2 mb-5 md:mb-0 flex items-center">
            <div>
              <h2 class="mb-3">{{ $t('$QRCode.Title') }}</h2>
              <p>
                {{ $t('$QRCode.Msg') }}
              </p>

              <div class="w-full flex justify-center md:justify-start">
                <vs-button
                  class="mt-base"
                  size="small"
                  @click="$router.push({ name: 'reviews' })"
                >
                  {{ $t('$QRCode.StartButton') }}
                </vs-button>
              </div>
            </div>
          </div>
        </div>
      </vx-card>
    </template>

    <template v-else>
      <base-ag-grid-header
        :rows-selected-count="rowsSelectedCountWithAllSelection"
        :filters-count="filtersAppliedCount"
        :filters-match.sync="filtersMatch"
        @reset-selection="setSelectedRowsNone()"
        @reset-filters="resetFilters()">
        <template v-slot:header-left>
          <items-per-page
            :count="count"
            :currentPage="currentPage"
            :paginationPageSize="paginationPageSize"
            @changed-page-size="paginationSetPageSize">
          </items-per-page>

          <list-action-drop-down
            v-if="isAnyRowsSelected"
            :options="multipleActionOptions"
            class="ml-3"
            @export="exportData()"
            @delete="confirmDeleteRecords()">
          </list-action-drop-down>
        </template>

        <template v-slot:header-right>
          <div class="w-full">
            <div class="flex w-full items-center justify-end">
              <div
                class="flex flex-col justify-center items-center cursor-pointer md:mr-base md:pr-base border-l-0 border-r-0 md:border-r-2 border-grey border-solid border-t-0 border-b-0"
                @click="$router.push('analytics/reviews')"
              >
                <p
                  class="text-center font-bold">
                  {{ overallRating | fixed_number(1) }}
                </p>
                <div
                  class="flex justify-center">
                  <star-rating
                    :rating="overallRating"
                    :increment="0.1"
                    :round-start-rating="false"
                    :show-rating="false"
                    read-only
                    :star-size="20"/>
                </div>
              </div>

              <page-header-actions
                class="hidden md:flex"
                :actions="headerActions"
                @action="onHeaderAction"
              />
            </div>
          </div>
        </template>
      </base-ag-grid-header>

      <selection-alert-info
        v-model="selectionPageAlert"
        class="mb-5"
        :entity="entity"
        :count="count"
        :rows-selected-count="rowsSelectedCount"
        @select-all="setSelectedRowsAll()"></selection-alert-info>

      <export-alert-info
        class="mb-5"
        :entity="$tc(`$Entities.${entity}`, 2)"
        :exporting.sync="exportingList"
        :exported.sync="exportedList"
        :fileUrl="urlFileExported"
        :send-email="exportSendEmail"/>

      <ag-grid-vue
        :key="agGridKey"
        ref="agGridTable"
        :domLayout="domLayout"
        :style="gridStyle"
        :components="components"
        :frameworkComponents="frameworkComponents"
        :gridOptions="gridOptions"
        class="ag-theme-material w-100 ag-grid-table"
        :columnDefs="columnDefs"
        :defaultColDef="defaultColDef"
        :column-types="columnTypes"
        :getRowNodeId="getRowNodeId"
        :autoParamsRefresh="true"
        rowSelection="multiple"
        :animateRows="true"
        :suppressRowClickSelection="true"
        rowModelType="infinite"
        :pagination="hasPagination"
        :paginationPageSize="paginationPageSize"
        :suppressPaginationPanel="suppressPaginationPanel"
        :overlayLoadingTemplate="overlayLoadingTemplate"
        :noRowsOverlayComponent="noRowsOverlayComponent"
        :noRowsOverlayComponentParams="noRowsOverlayComponentParams"
        :maxConcurrentDatasourceRequests="maxConcurrentDatasourceRequests"
        :cacheBlockSize="cacheBlockSize"
        :maxBlocksInCache="maxBlocksInCache"
        :getRowHeight="getRowHeight"
        @selection-changed="onSelectionChanged"
        @grid-ready="onGridReady"
        @model-updated="onModelUpdate"
        @grid-size-changed="onGridSizeChanged"
        @first-data-rendered="onFirstDataRendered"
        @row-double-clicked="onRowDoubleClicked"
        @filter-changed="onFilterChanged"
        @pagination-changed="onPaginationChanged"
        @row-clicked="onRowClicked"
      >
      </ag-grid-vue>

      <!-- Mobile Sticky Bottom Bar (inline - scoped styles require this to be in parent) -->
      <div class="sticky-bottom-bar-icons md:hidden">
        <!-- Rating filter dropdown (mobile) -->
        <vs-dropdown
          vs-custom-content
          vs-trigger-click
          class="mobile-bar-btn mobile-bar-btn-simple mobile-rating-dropdown-trigger"
        >
          <div class="mobile-bar-btn-content">
            <i class="feather icon-star mobile-bar-icon mobile-rating-star-icon"></i>
            <span class="mobile-btn-label">Rating</span>
          </div>

          <vs-dropdown-menu class="mobile-rating-dropdown gp-sticky-dropdown-menu">
            <ul class="mobile-rating-options">
              <li
                class="mobile-rating-option"
                :class="{ active: mobileRatingMinStars === null }"
                @click="applyMobileRatingFilter(null)"
              >
                <div class="mobile-rating-option-indicator">
                  <i class="feather icon-star mobile-rating-option-star"></i>
                </div>
                <div class="mobile-rating-option-content">
                  <div class="mobile-rating-option-title">All ratings</div>
                </div>
                <feather-icon
                  v-if="mobileRatingMinStars === null"
                  icon="CheckIcon"
                  svgClasses="h-5 w-5 text-primary"></feather-icon>
              </li>

              <li
                v-for="n in [5,4,3,2,1]"
                :key="`rating-${n}`"
                class="mobile-rating-option"
                :class="{ active: mobileRatingMinStars === n }"
                @click="applyMobileRatingFilter(n)"
              >
                <div class="mobile-rating-option-indicator">
                  <i class="feather icon-star mobile-rating-option-star"></i>
                </div>
                <div class="mobile-rating-option-content">
                  <div class="mobile-rating-option-title">{{ n }}+ stars</div>
                </div>
                <feather-icon
                  v-if="mobileRatingMinStars === n"
                  icon="CheckIcon"
                  svgClasses="h-5 w-5 text-primary"></feather-icon>
              </li>
            </ul>
          </vs-dropdown-menu>
        </vs-dropdown>

        <!-- Form button -->
        <button
          class="mobile-bar-btn mobile-bar-btn-simple"
          type="button"
          @click="$router.push({ name: 'reviews-public-form' })"
        >
          <i class="feather icon-file-text mobile-bar-icon"></i>
          <span class="mobile-btn-label">Form</span>
        </button>

        <!-- Config button -->
        <button
          class="mobile-bar-btn mobile-bar-btn-simple"
          type="button"
          @click="createOrEdit = true"
        >
          <i class="feather icon-settings mobile-bar-icon"></i>
          <span class="mobile-btn-label">Config</span>
        </button>

        <!-- Refresh button -->
        <button
          class="mobile-bar-btn mobile-bar-btn-simple mobile-bar-btn-refresh"
          type="button"
          @click="refreshData()"
        >
          <i class="feather icon-refresh-cw mobile-bar-icon"></i>
          <span class="mobile-btn-label">Refresh</span>
        </button>
      </div>
    </template>

    <vs-popup
      :title="exportModalTitle"
      :active.sync="activeModalToExport">
      <transition name="zoom-fade">
        <export-json-to-excel
          v-if="showExportComponent"
          :columns="getColumnsToExport"
          :data="rowsSelected"
          @close="activeModalToExport=false"
          @export="onExport">
        </export-json-to-excel>
      </transition>
    </vs-popup>

    <vs-popup
      :title="detailsModalTitle"
      :active.sync="activeDetailsModal">
      <transition name="zoom-fade">
        <div v-if="activeDetailsModal">
          <cell-renderer-contact
            class="text-center"
            :review-payload="reviewDetails"
            :rating-centered="true"
          />

          <p class="mt-1 text-center">{{ reviewDetails.updatedAt | date_format(
            'DD MMM YYYY',
            true,
            this.tenantAccountDetailsTimezone,
          ) }}</p>

          <p class="mt-base text-center">{{ reviewDetails.review }}</p>
        </div>
      </transition>
    </vs-popup>
  </div>
</template>

<script>
import '@/assets/scss/vuexy/extraComponents/agGridStyleOverride.scss';
import { AgGridVue } from 'ag-grid-vue';
import StarRating from 'vue-star-rating';

// Services
import reviewService from '@/api/modules/review.service';

// Mixins
import commonListCreateOrEditWithAgGrid from '@/views/modules/_mixins/commonListCreateOrEditWithAgGrid';

// Cell Renderer
import CellRendererActions from '@/views/modules/_components/cell-renderer/SingleCellRendererActions.vue';
import CellRendererRating from '@/views/modules/reviews/ReviewsSettingsCellRendererRating.vue';
import CellRendererContact from '@/views/modules/reviews/ReviewsSettingsCellRendererContact.vue';

// Custom Components
import AgGridSelectionHeader from '@/views/modules/_components/AgGridSelectionHeader';
import ReviewsSettingsCreateOrEdit from '@/views/modules/reviews/ReviewsSettingsCreateOrEdit.vue';
import ListActionDropDown from '@/views/modules/_components/ListActionDropDown.vue';
import BaseAgGridHeader from '@/views/modules/_components/BaseAgGridHeader.vue';
import ExportAlertInfo from '@/views/modules/_components/ExportAlertInfo.vue';
import ItemsPerPage from '@/views/modules/_components/ItemsPerPage.vue';
import SelectionAlertInfo from '@/views/modules/SelectionAlertInfo.vue';
import ExportJsonToExcel from '@/views/modules/_components/ExportJsonToExcel.vue';
import CellRendererLargeText from '@/views/modules/_components/cell-renderer/CellRendererLargeTextModal.vue';
import CustomNoRowsOverlay from '@/views/modules/_components/customAgGridNoRowsOverlay';
import PageHeaderActions from '@/views/modules/_components/PageHeaderActions.vue';
import ReviewsQRCode from '@/views/modules/reviews/ReviewsQRCode.vue';
/**
 * @module views/modules/reviews/ReviewsSettings
 * @author Dilan Useche <dilan8810@gmail.com>
 */
export default {
  name: 'ReviewsSettings',
  i18n: {
    messages: {
      en: {
        GoogleReviewSettings: 'Google reviews',
        ThereAreNoReviews: 'No reviews to display',
        $QRCode: {
          Title: 'QR code for review access',
          Msg: 'This is your downloadable QR code for your business to display on-site. Customers can scan this code to open your review form and leave feedback in seconds.',
          StartButton: 'Let\'s get started',
        },
        $NoRowsOverlay: {
          Title: 'No reviews to display',
          Text: 'There are no reviews to view right now. Create a new campaign to start collecting reviews.',
          Button: 'Create new campaign',
        },
        DetailsModalTitle: 'Review from {name}',
      },
    },
  },
  components: {
    ExportJsonToExcel,
    SelectionAlertInfo,
    ItemsPerPage,
    ExportAlertInfo,
    BaseAgGridHeader,
    ListActionDropDown,
    AgGridVue,
    StarRating,
    ReviewsSettingsCreateOrEdit,
    PageHeaderActions,
    ReviewsQRCode,
    // eslint-disable-next-line vue/no-unused-components
    CellRendererActions,
    // eslint-disable-next-line vue/no-unused-components
    CellRendererRating,
    // eslint-disable-next-line vue/no-unused-components
    CellRendererLargeText,
    CellRendererContact,
  },
  mixins: [commonListCreateOrEditWithAgGrid],
  data() {
    return {
      entity: this.$enums.Entity.REVIEW,
      exportCollectionFunction: this.exportReviews,
      overviewData: null,
      preferFitColumns: true,
      visibleTabletColumns: ['contact', 'feedback'],
      visibleLaptopColumns: ['contact', 'feedback', 'updatedAt'],
      mobileRatingMinStars: null,
      gridOptions: {
        rowHeight: 58,
        context: {
          componentParent: this,
        },
      },
      reviewDetails: null,
      noRowsMessage: this.$t('ThereAreNoReviews'),
      activeDetailsModal: false,

      columnDefs: [
        {
          colId: 'contact',
          headerName: this.$t('$General.Contact'),
          field: 'name',
          filter: true,
          sortable: false,
          minWidth: 350,
          maxWidth: 450,
          checkboxSelection: true,
          headerComponentParams: { checkboxSelection: true },
          cellClass: 'mobile-caret',
          cellRendererFramework: 'CellRendererContact',
        },
        {
          colId: 'score',
          headerName: this.$t('$General.Rating'),
          field: 'score',
          filter: 'agNumberColumnFilter',
          width: 250,
          headerClass: 'flex justify-center',
          cellRendererFramework: 'CellRendererRating',
        },
        {
          colId: 'feedback',
          headerName: this.$t('$General.Feedback'),
          field: 'review',
          filter: true,
          sortable: false,
          minWidth: 250,
          maxWidth: 1906,
          cellClass: 'flex items-center',
          cellRendererFramework: 'CellRendererLargeText',
        },
      ],
      components: {
        CellRendererActions,
        CellRendererLargeText,
      },
      frameworkComponents: {
        agColumnHeader: AgGridSelectionHeader,
        customNoRowsOverlay: CustomNoRowsOverlay,
      },

      createOrEdit: false,
      qrCode: false,
    };
  },
  computed: {
    overallRating() {
      return this.overviewData ? this.overviewData.rating : 0;
    },
    multipleActionOptions() {
      return this.defaultMultipleActionOptions.filter(
        (option) => option.event !== this.$enums.Operation.DELETE,
      );
    },
    googleReviewSettingsText() {
      return this.gpMqIsMobileOrTablet ? '' : this.$t('GoogleReviewSettings');
    },
    headerActions() {
      return [
        {
          id: 'publicForm',
          tooltip: 'Public form',
          kind: 'icon',
          icon: 'icon-file-text',
        },
        {
          id: 'googleReviews',
          tooltip: this.$t('GoogleReviewSettings'),
          kind: 'icon',
          icon: 'icon-settings',
        },
        {
          id: 'refresh',
          tooltip: 'Refresh',
          kind: 'icon',
          icon: 'icon-refresh-cw',
        },
      ];
    },
    detailsModalTitle() {
      if (!this.reviewDetails) return '';

      return this.$t('DetailsModalTitle', {
        name: this.reviewDetails.name || this.$t('$General.Anonymous').toLowerCase(),
      });
    },
  },
  created() {
    this.fetchOverviewData();
    this.handleRouteCheck();
  },
  beforeMount() {
    const $this = this;
    this.noRowsOverlayComponent = 'customNoRowsOverlay';
    this.noRowsOverlayComponentParams = {
      title: this.$t('$NoRowsOverlay.Title'),
      text: this.$t('$NoRowsOverlay.Text'),
      buttonText: this.$t('$NoRowsOverlay.Button'),
      async actionButtonClicked() {
        await $this.$router.push({
          name: 'sms-campaigns-create',
        });
      },
    };

    this.gridOptions.rowHeight = this.gpMqIsLessOrEqualLaptopMedium ? 112 : 84;
  },
  methods: {
    async fetchOverviewData() {
      const { data } = await reviewService.getReviewsOverview();
      this.overviewData = data;
    },
    async fetchAgGridData(agGridParams) {
      const params = {
        populate: [
          {
            path: 'contact',
            select: 'id name email phoneInternational',
          },
        ],
        ...agGridParams,
      };
      let filters = {
        ...params.filters,
      };
      let { filtersMatch } = params;

      const { data } = await reviewService.getAll({
        ...params,
        filters,
        filtersMatch,
      });
      return data;
    },
    async exportReviews(exportInfo) {
      const resp = await reviewService.exportFile(exportInfo);
      return resp.data;
    },
    getRowHeight(params) {
      return this.gpMqIsLessOrEqualLaptopMedium ? 108 : 80;
    },
    async refreshData() {
      await this.resetDataSourceAndSelection();
      await this.fetchOverviewData();
    },
    applyMobileRatingFilter(minStars) {
      this.mobileRatingMinStars = minStars;

      // Guard: grid might not be initialised yet
      if (!this.gridApi) return;

      const model = this.gridApi.getFilterModel() || {};

      if (minStars === null) {
        // Clear only the score filter, preserve any other filters
        if (model.score) delete model.score;
      } else {
        model.score = {
          filterType: 'number',
          type: 'greaterThanOrEqual',
          filter: minStars,
        };
      }

      this.gridApi.setFilterModel(model);
    },
    handleRouteCheck() {
      this.qrCode = this.$route.name === 'reviews-public-form';
      if (this.qrCode) {
        this.createOrEdit = false;
      }
    },
    onHeaderAction({ id }) {
      switch (id) {
        case 'publicForm':
          this.$router.push({ name: 'reviews-public-form' });
          break;
        case 'googleReviews':
          this.createOrEdit = true;
          break;
        case 'refresh':
          this.refreshData();
          break;
        default:
          break;
      }
    },
    onRowClicked(params) {
      if (this.gpMqIsMobile) {
        this.reviewDetails = params.node.data;
        this.activeDetailsModal = true;
      }
    },
  },
  watch: {
    '$route'() {
      this.handleRouteCheck();
    },
  },
};
</script>

<style scoped lang="scss">
// Mobile Sticky Bottom Bar styles
.sticky-bottom-bar-icons {
  position: -webkit-sticky;
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top: 2px solid #E5E7EB;
  padding: 8px 12px;
  display: grid;
  grid-template-columns: repeat(4, 1fr); // Rating + Form + Config + Refresh
  gap: 8px;
  box-shadow: 0 -4px 6px -1px rgba(0, 0, 0, 0.1);
  z-index: 100;
  margin: 0 -1.5rem -1.5rem; // Compensate for vx-card padding
  padding-bottom: calc(8px + env(safe-area-inset-bottom, 0px));
}

.mobile-rating-star-icon {
  color: #F59E0B; // amber
}

/* Dropdown menu needs to sit above the sticky bar on mobile */
::v-deep .mobile-rating-dropdown {
  z-index: 3000;
}

.mobile-rating-options {
  list-style: none;
  margin: 0;
  padding: 0;
  min-width: 220px;
}

.mobile-rating-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  cursor: pointer;

  &:hover {
    background: rgba(139, 92, 246, 0.06);
  }
}

.mobile-rating-option-indicator {
  width: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mobile-rating-option-star {
  color: #F59E0B;
  font-size: 14px;
}

.mobile-rating-option-content {
  flex: 1;
}

.mobile-rating-option-title {
  font-weight: 600;
  font-size: 13px;
  color: #111827;
}

.mobile-bar-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px 6px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.15s ease;
  color: #6B7280;
  font-family: inherit;
  outline: none;

  &:active {
    transform: scale(0.95);
    background: rgba(139, 92, 246, 0.1);
  }
}

.mobile-bar-icon {
  font-size: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 6px auto;
  width: 22px;
  height: 22px;
}

.mobile-btn-label {
  font-size: 11px;
  font-weight: 500;
  text-align: center;
  line-height: 1;
}

.mobile-bar-btn-simple {
  @media (hover: hover) {
    &:hover {
      background: rgba(139, 92, 246, 0.05);
    }
  }

  &:active {
    background: rgba(139, 92, 246, 0.1) !important;
  }
}

.mobile-bar-btn-refresh {
  @media (hover: hover) {
    &:hover {
      background: rgba(139, 92, 246, 0.05);
    }
  }

  &:active {
    background: rgba(139, 92, 246, 0.1) !important;
  }

  &:not(:active):not(:hover) {
    background: transparent !important;
  }
}
</style>
